import { isSupabaseConfigured, supabase } from "../lib/supabase";

const STORAGE_KEY = "almoune_supplier_invoices";

const normalizeInvoice = (i) => ({
  id: i.id || `sinv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  purchaseId: i.purchaseId || null,
  supplierId: i.supplierId || "",
  supplierName: i.supplierName || "",
  invoiceNumber: i.invoiceNumber || "",
  invoiceDate: i.invoiceDate || new Date().toISOString().split("T")[0],
  dueDate: i.dueDate || null,
  items: Array.isArray(i.items) ? i.items : [],
  totalHT: Number(i.totalHT) || 0,
  totalTVA: Number(i.totalTVA) || 0,
  totalTTC: Number(i.totalTTC) || 0,
  status: i.status || "pending",
  paidDate: i.paidDate || null,
  notes: i.notes || "",
  createdAt: i.createdAt || new Date().toISOString(),
});

const readCache = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeInvoice);
  } catch {
    return [];
  }
};

const writeCache = (invoices) => {
  const normalized = invoices.map(normalizeInvoice);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
};

export const getSupplierInvoices = () => readCache();

export const loadSupplierInvoices = async () => {
  const cached = readCache();
  if (!isSupabaseConfigured || !supabase) return cached;
  try {
    const { data, error } = await supabase.from("supplier_invoices").select("*").order("created_at", { ascending: false });
    if (error || !data) return cached;
    return writeCache(data.map((row) => ({
      id: row.id,
      purchaseId: row.purchase_id,
      supplierId: row.supplier_id,
      supplierName: row.supplier_name,
      invoiceNumber: row.invoice_number,
      invoiceDate: row.invoice_date,
      dueDate: row.due_date,
      items: typeof row.items === "string" ? JSON.parse(row.items) : row.items || [],
      totalHT: Number(row.total_ht) || 0,
      totalTVA: Number(row.total_tva) || 0,
      totalTTC: Number(row.total_ttc) || 0,
      status: row.status,
      paidDate: row.paid_date,
      notes: row.notes,
      createdAt: row.created_at,
    })));
  } catch (err) {
    console.error("Failed to load supplier invoices:", err);
    return cached;
  }
};

export const saveSupplierInvoice = async (invoice) => {
  const normalized = normalizeInvoice(invoice);
  let all = readCache();
  const idx = all.findIndex((i) => i.id === normalized.id);
  if (idx >= 0) all[idx] = normalized;
  else all.push(normalized);
  writeCache(all);

  if (isSupabaseConfigured && supabase) {
    const payload = {
      id: normalized.id,
      purchase_id: normalized.purchaseId,
      supplier_id: normalized.supplierId,
      supplier_name: normalized.supplierName,
      invoice_number: normalized.invoiceNumber,
      invoice_date: normalized.invoiceDate,
      due_date: normalized.dueDate,
      items: normalized.items,
      total_ht: normalized.totalHT,
      total_tva: normalized.totalTVA,
      total_ttc: normalized.totalTTC,
      status: normalized.status,
      paid_date: normalized.paidDate,
      notes: normalized.notes,
      created_at: normalized.createdAt,
    };
    try {
      await supabase.from("supplier_invoices").upsert(payload, { onConflict: "id" });
    } catch (err) {
      console.error("Failed to save supplier invoice to Supabase:", err);
    }
  }
  return all;
};

export const deleteSupplierInvoice = async (id) => {
  let all = readCache();
  all = all.filter((i) => i.id !== id);
  writeCache(all);

  if (isSupabaseConfigured && supabase) {
    try {
      await supabase.from("supplier_invoices").delete().eq("id", id);
    } catch (err) {
      console.error("Failed to delete supplier invoice from Supabase:", err);
    }
  }
  return all;
};

export const createInvoiceFromPurchase = (purchase, supplier) => {
  const items = (purchase.items || []).map((item) => {
    const tvaRate = item.tvaRate !== undefined ? item.tvaRate : 0.055;
    const unitCost = item.costPrice !== undefined ? item.costPrice : (item.purchasePrice !== undefined ? item.purchasePrice : 0);
    return {
      productId: item.productId || item.id,
      productName: item.productName || item.name || "Produit Inconnu",
      quantity: item.quantity || 1,
      unitCost,
      tvaRate,
      lineTotal: (item.quantity || 1) * unitCost,
    };
  });
  
  const totalHT = items.reduce((sum, item) => sum + item.lineTotal, 0);
  const totalTVA = items.reduce((sum, item) => sum + (item.lineTotal * item.tvaRate), 0);
  const totalTTC = totalHT + totalTVA;

  return normalizeInvoice({
    purchaseId: purchase.id,
    supplierId: supplier.id,
    supplierName: supplier.name,
    invoiceNumber: "INV-" + (purchase.orderNumber || purchase.id.slice(0, 8)),
    invoiceDate: new Date().toISOString().split("T")[0],
    dueDate: null,
    items,
    totalHT,
    totalTVA,
    totalTTC,
    status: "pending",
  });
};
